import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import CustomButton from '../../../common/CustomButton/CustomButton';
import CustomCheckbox from '../../../common/CustomCheckbox/CustomCheckbox';
import CustomForm from '../../../common/CustomForm/CustomForm';
import CustomInput from '../../../common/CustomInput/CustomInput';
import { PASSWORD_REGEX } from '../../../core/constants';
import { useUserContext } from '../../../core/context/user.context';
import { isEmpty, isValidEmail } from '../../../core/helpers';
import { useFormCheckbox } from '../../../core/hooks/useFormCheckbox';
import { useFormInput } from '../../../core/hooks/useFormInput';
import './LoginForm.css';

type TStep = 'email' | 'password';

function LoginForm() {
  const [searchParams] = useSearchParams();
  const searchParamsEmail = searchParams.get('email');
  const [step, setStep] = useState<TStep>('email');

  const email = useFormInput(
    searchParamsEmail ? decodeURIComponent(searchParamsEmail) : '',
    (value) => isValidEmail(value),
  );
  const password = useFormInput('', (value) => !isEmpty(value) && PASSWORD_REGEX.test(value));
  const remember = useFormCheckbox(false);
  const { login, loginLoading, checkIfSSO, ssoCheckLoading } = useUserContext();

  const handleLogin = useCallback(() => {
    login(email, password, remember);
  }, [email, login, password, remember]);

  const handleSSOCheck = useCallback(async () => {
    const sso = await checkIfSSO(email);
    if (sso?.force_sso && sso?.sso_url) {
      window.location.href = sso.sso_url;
    } else {
      setStep('password');
    }
  }, [checkIfSSO, email]);

  const handleStepBack = useCallback(async () => {
    setStep('email');
    email.setValue('');
  }, [email]);

  return (
    <section className="login-form">
      {step !== 'email' ? null : (
        <CustomForm className="flex gap-xs" inputs={[email]} onSubmit={handleSSOCheck}>
          <>
            <CustomInput
              id="login-email"
              type="text"
              name="email"
              placeholder=""
              label="E-mail address"
              input={email}
              emptyMessage="Please fill in your email"
              invalidMessage="Please fill in a valid email address"
            />
            <CustomButton className="login-form__cta btn" type="submit" loading={ssoCheckLoading}>
              Next
            </CustomButton>
          </>
        </CustomForm>
      )}
      {step !== 'password' ? null : (
        <CustomForm className="flex gap-xs" inputs={[email, password]} onSubmit={handleLogin}>
          <>
            <CustomInput
              id="login-password"
              type="password"
              name="password"
              placeholder=""
              label="Password"
              input={password}
              link={{ text: 'Forgot your password?', href: '/forgot-password' }}
              emptyMessage="Please fill in your password"
              invalidMessage="Please fill in a valid password, minimum eight characters, one uppercase letter, one lowercase letter, one digit and one special character."
            />
            <CustomCheckbox id="remember" name="remember" label="Remember me" input={remember} />
            <CustomButton className="login-form__cta btn" type="submit" loading={loginLoading}>
              Log in
            </CustomButton>
            <CustomButton className="login-form__back link" type="button" onClick={handleStepBack}>
              Change email
            </CustomButton>
          </>
        </CustomForm>
      )}
    </section>
  );
}

export default LoginForm;
